import type { TFunction } from 'i18next'

import { CAP, can } from '@/lib/can'
import type { QuoteSectionKey } from '@/features/quotes/sectionSpy'
import type { CurrentUser, QuoteDetail } from '@/types/domain'

/* «Готовность к отправке», as ONE pure reading of the quote (S-3, L-4).
 *
 * Two surfaces show it — the readiness card in the xl rail and the markers on
 * the sticky section bar — and both read the rows built here, so the card and
 * the bar cannot disagree about which section still needs work.
 *
 * Honest about the server's send rule. `quote.send` refuses unless the reader
 * holds the capability AND may read the client's contacts, the quote is a
 * draft, it has at least one position, its checklist is closed (`sendable`)
 * and there is an address to send to (`clientContactEmail`: the named
 * contact's, or with no named contact the client's first contact with one).
 * Those are the two REQUIRED rows. The route, the costing and the validity are
 * ADVISABLE: the server sends without them, and a row that claimed otherwise
 * would be the card inventing a rule. The issuing entity's branding is checked
 * at render and cannot be known here, so no row speaks for it.
 *
 * There is deliberately no "ready to send" verdict in here. Whether the offer
 * may go out is `sendBlockedReason` (quoteText.ts): the server's
 * `record.sendable`, which covers the quote's own state only, plus the
 * recipient from `recipientState` below, in the server's order. The page's
 * ready banner and every Send on it read that one answer.
 */

export type RecipientState =
  | { kind: 'ok'; email: string }
  | { kind: 'noContactAccess' }
  | { kind: 'noRecipient' }

/** Where the offer would go, as the reader may know it.
 *
 *  The capability is asked FIRST (Q4): a role without `directory.contact.read`
 *  is served no address and the server refuses its send, so "the client has no
 *  email" would send it to the directory to add an address that already
 *  exists. `OfferSendSheet` words its refusal from the same answer. */
export function recipientState(
  quote: Pick<QuoteDetail, 'clientContactEmail'>,
  user: CurrentUser | undefined,
): RecipientState {
  if (!can(user, CAP.directoryContactRead)) return { kind: 'noContactAccess' }
  const email = quote.clientContactEmail?.trim() ?? ''
  return email === '' ? { kind: 'noRecipient' } : { kind: 'ok', email }
}

/** The sentence that says why there is nowhere to send — the send sheet's
 *  refusal and the client block's recipient line. Undefined when there is an
 *  address. */
export function recipientRefusal(
  state: RecipientState,
  t: TFunction,
): string | undefined {
  switch (state.kind) {
    case 'noContactAccess':
      return t('quotes.offerSend.noContactAccess')
    case 'noRecipient':
      return t('quotes.offerSend.noRecipient')
    case 'ok':
      return undefined
  }
}

export type ReadinessRowKey = 'cargo' | 'recipient' | 'route' | 'pricing' | 'validity'

/** Why a row is not ticked. A code, worded by `gapText`. */
export type ReadinessGap =
  | { code: 'issues'; count: number }
  | { code: 'noPositions' }
  | { code: 'noContactAccess' }
  | { code: 'noRecipient' }
  | { code: 'noRoute' }
  | { code: 'noCostLines' }
  | { code: 'pricingRequested' }
  | { code: 'noValidity' }

export interface ReadinessRow {
  key: ReadinessRowKey
  /** The section the row's link scrolls to. */
  section: QuoteSectionKey
  /** Required by the server's send rule, or merely advisable. */
  required: boolean
  /** Absent when the row is ticked. */
  gap?: ReadinessGap
}

/** The five rows, in the card's order: the two required, then the three
 *  advisable. */
export function quoteReadiness(
  record: QuoteDetail,
  user: CurrentUser | undefined,
): ReadinessRow[] {
  const cargo = record.cargo ?? []
  const issues = (record.checklist ?? []).length
  const recipient = recipientState(record, user)
  const costLines = (record.costLines ?? []).length

  return [
    {
      key: 'cargo',
      section: 'client',
      required: true,
      /* No positions first: an empty checklist on a quote with no cargo means
         nothing has been checked, not that everything passed. */
      gap:
        cargo.length === 0
          ? { code: 'noPositions' }
          : issues > 0
            ? { code: 'issues', count: issues }
            : undefined,
    },
    {
      key: 'recipient',
      section: 'client',
      required: true,
      gap: recipient.kind === 'ok' ? undefined : { code: recipient.kind },
    },
    {
      key: 'route',
      section: 'route',
      required: false,
      gap: (record.routeOptions ?? []).length > 0 ? undefined : { code: 'noRoute' },
    },
    {
      key: 'pricing',
      section: 'pricing',
      required: false,
      /* «Ждёт расчёта» is a colleague still pricing it: the figures on the
         lines may change. `none` is nobody asked, which is a salesperson
         costing it alone and waiting on nobody. */
      gap:
        costLines === 0
          ? { code: 'noCostLines' }
          : record.pricing.state === 'requested'
            ? { code: 'pricingRequested' }
            : undefined,
    },
    {
      key: 'validity',
      section: 'terms',
      required: false,
      gap: record.validUntil ? undefined : { code: 'noValidity' },
    },
  ]
}

export interface SectionMarker {
  /** A count of checklist issues, or `true` for a gap that has no count. */
  badge: number | true
  /** `attention` when a REQUIRED row is unticked, `neutral` otherwise. */
  tone: 'neutral' | 'attention'
  /** The unticked rows behind the marker — what its label says. */
  rows: ReadinessRow[]
}

/** One marker per section with at least one unticked row, and none for the
 *  others. Built from the rows rather than re-reading the quote, which is what
 *  keeps the bar and the card in agreement. */
export function sectionMarkers(
  rows: readonly ReadinessRow[],
): Partial<Record<QuoteSectionKey, SectionMarker>> {
  const markers: Partial<Record<QuoteSectionKey, SectionMarker>> = {}

  for (const row of rows) {
    if (!row.gap) continue
    const marker = markers[row.section] ?? { badge: true, tone: 'neutral', rows: [] }
    marker.rows.push(row)
    if (row.required) marker.tone = 'attention'
    if (row.gap.code === 'issues') marker.badge = row.gap.count
    markers[row.section] = marker
  }

  return markers
}

/** The words for a gap — short, because they stand beside a row label. */
export function gapText(gap: ReadinessGap, t: TFunction): string {
  switch (gap.code) {
    case 'issues':
      return t('quotes.readiness.gap.issues', { count: gap.count })
    case 'noPositions':
      return t('quotes.readiness.gap.noPositions')
    case 'noContactAccess':
      return t('quotes.readiness.gap.noContactAccess')
    case 'noRecipient':
      return t('quotes.readiness.gap.noRecipient')
    case 'noRoute':
      return t('quotes.readiness.gap.noRoute')
    case 'noCostLines':
      return t('quotes.readiness.gap.noCostLines')
    case 'pricingRequested':
      // The glossary's term for the state, not a second wording of it.
      return t('quotes.pricingState.states.requested')
    case 'noValidity':
      return t('quotes.readiness.gap.noValidity')
  }
}

/** A row's label, from the catalogue. */
export function readinessRowLabel(key: ReadinessRowKey, t: TFunction): string {
  return t(`quotes.readiness.row.${key}`)
}
